import { formatDate } from '@/services/utils'
import { rgb, StandardFonts } from 'pdf-lib'
import type { PDFDocument, PDFFont, PDFPage } from 'pdf-lib'
import { computed } from 'vue'
import i18n from '@/i18n'
import type { LoadedImages } from './images'
import { Implemented, NeedsAttention, NotImplemented, PartiallyImplemented } from '@/models/enums-generated'

export const A4_WIDTH = 595.28
export const A4_HEIGHT = 841.89
export const HEADER_HEIGHT = 60
export const FOOTER_HEIGHT = 50
export const MARGIN = 40
export const CONTENT_START_Y = A4_HEIGHT - HEADER_HEIGHT - 30

export const TITLE_PAGE = {
	TITLE_Y: 270,
	TITLE_SIZE: 38,
	DESC_START_Y: 320,
	DESC_SIZE: 14,
	DESC_LINE_HEIGHT: 22,
	CATALOGS_START_Y: 550,
	CATALOG_TITLE_SIZE: 16,
	META_START_Y: 430,
	META_SPACING: 35,
	LOGO_Y: 800,
	LOGO_HEIGHT: 30,
}

export const SPACING = {
	section: 15,
	line: 12,
	block: 18,
	separator: 20,
}

const currentDate = computed(() => formatDate(new Date()))

let titleTextFont: PDFFont
let bodyFont: PDFFont

export async function initializeFonts(pdfDoc: PDFDocument) {
	titleTextFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold)
	bodyFont = await pdfDoc.embedFont(StandardFonts.Helvetica)
}

export function getTitleFont() {
	return titleTextFont
}

export function getBodyFont() {
	return bodyFont
}

export const COLORS = {
	RED: rgb(0.75, 0.05, 0.05),
	LIGHT_RED: rgb(0.98, 0.94, 0.94),
	DARK_RED: rgb(0.6, 0.04, 0.04),
	BLACK: rgb(0, 0, 0),
	GRAY: rgb(0.5, 0.5, 0.5),
	LIGHT_GRAY: rgb(0.85, 0.85, 0.85),
	WHITE: rgb(1, 1, 1),
	GREEN: rgb(0.38, 0.85, 0.22),
	ORANGE: rgb(1, 0.58, 0),
	BLUE: rgb(0, 0, 1),
}

export interface DrawContext {
	pdfDoc: PDFDocument
	page: PDFPage
	currentY: number
	currentPageNum: number
	images: LoadedImages
	titleText: string
}

const statusColors = {
	[Implemented]: COLORS.GREEN,
	[PartiallyImplemented]: COLORS.ORANGE,
	[NotImplemented]: COLORS.RED,
	[NeedsAttention]: COLORS.BLUE,
}

export const getStatusColor = (status?: string) => statusColors[status as keyof typeof statusColors] || COLORS.LIGHT_GRAY

export function hexToRgb(hex: string) {
	return rgb(parseInt(hex.slice(1, 3), 16) / 255, parseInt(hex.slice(3, 5), 16) / 255, parseInt(hex.slice(5, 7), 16) / 255)
}

export function ensureSpace(ctx: DrawContext, needed: number): DrawContext {
	if (ctx.currentY - needed < MARGIN + FOOTER_HEIGHT) {
		ctx.currentPageNum++
		ctx.page = ctx.pdfDoc.addPage([A4_WIDTH, A4_HEIGHT])
		ctx.currentY = CONTENT_START_Y
		drawModernHeader(ctx.page, ctx.images, ctx.titleText)
	}
	return ctx
}

export function htmlToText(html: string): string {
	const doc = new DOMParser().parseFromString(html, 'text/html')
	return doc.body.textContent || ''
}

export function sanitizeText(text: string): string {
	return htmlToText(text)
		.replace(/[\n\r]+/g, ' ')
		.replace(/\s+/g, ' ')
		.replace(/[^\x20-\x7E\u00A0-\u00FF\u20AC]/g, '')
		.trim()
}

export function wrapText(text: string, maxWidth: number, size: number): string[] {
	const words = sanitizeText(text).split(' ').filter(Boolean)
	const lines: string[] = []
	let current = ''
	for (const word of words) {
		const test = current ? `${current} ${word}` : word
		if (bodyFont.widthOfTextAtSize(test, size) > maxWidth && current) {
			lines.push(current)
			current = word
		} else {
			current = test
		}
	}
	if (current) lines.push(current)
	return lines
}

export function drawText(page: PDFPage, x: number, y: number, text: string, size: number, color = COLORS.BLACK, bold = false) {
	const font = bold ? titleTextFont : bodyFont
	page.drawText(sanitizeText(text), { x, y, size, font, color })
	return font.widthOfTextAtSize(sanitizeText(text), size)
}

export function drawModernHeader(page: PDFPage, images: LoadedImages, titleText: string) {
	for (let i = 0; i < 5; i++) {
		const opacity = 0.15 - i * 0.02
		const height = HEADER_HEIGHT / 5
		page.drawRectangle({
			x: 0,
			y: A4_HEIGHT - HEADER_HEIGHT + i * height,
			width: A4_WIDTH,
			height,
			color: COLORS.LIGHT_RED,
			opacity,
		})
	}
	page.drawRectangle({ x: 0, y: A4_HEIGHT - HEADER_HEIGHT - 2, width: A4_WIDTH, height: 2, color: COLORS.RED, opacity: 0.3 })
	page.drawLine({
		start: { x: 0, y: A4_HEIGHT - HEADER_HEIGHT },
		end: { x: A4_WIDTH, y: A4_HEIGHT - HEADER_HEIGHT },
		thickness: 3,
		color: COLORS.RED,
	})
	drawText(page, 60, A4_HEIGHT - 40, titleText || i18n.global.t('pdf.riskReport'), 35, COLORS.DARK_RED, true)
}

export function drawFooter(page: PDFPage, index: number, total: number, images: LoadedImages) {
	page.drawLine({ start: { x: 0, y: FOOTER_HEIGHT }, end: { x: A4_WIDTH, y: FOOTER_HEIGHT }, thickness: 1, color: COLORS.LIGHT_GRAY })
	const logoH = 18
	const logoW = logoH * images.logoFullAspect
	page.drawImage(images.logoFullPng, { x: MARGIN, y: (FOOTER_HEIGHT - logoH) / 2, width: logoW, height: logoH })
	const pageText = `${index + 1} / ${total}`
	const pageWidth = bodyFont.widthOfTextAtSize(pageText, 10)
	drawText(page, (A4_WIDTH - pageWidth) / 2, FOOTER_HEIGHT / 2 - 3, pageText, 10, COLORS.GRAY)
	drawText(page, A4_WIDTH - MARGIN - 80, FOOTER_HEIGHT / 2 - 3, currentDate.value, 9, COLORS.GRAY)
}

export function drawSeparator(page: PDFPage, y: number, thickness = 0.5) {
	page.drawLine({ start: { x: MARGIN, y }, end: { x: A4_WIDTH - MARGIN, y }, thickness, color: COLORS.LIGHT_GRAY })
}
